import { MutationCache } from '@tanstack/react-query';
import { getAnalyticsClient } from './client';
import type { EventName, EventPayload } from './types';

/**
 * Mapping from a generated Orval mutationKey to an analytics dispatcher.
 * Empty by design — the product funnel dispatchers were removed. The
 * mechanism stays so re-adding a tracked mutation is one append here (keyed
 * on the Orval mutationKey) plus its event in `types.ts`, then
 * `yarn analytics:snapshot` — no per-feature edits.
 *
 * `data` is the mutation's resolved value, `vars` are the variables the
 * caller passed to `mutate()`. Dispatchers cherry-pick what they need.
 */
type Dispatcher = (
  outcome: 'success' | 'error',
  data: unknown,
  vars: unknown,
  error: unknown,
) => Array<{ name: EventName; properties: EventPayload<EventName> }> | undefined;

const dispatchers: Record<string, Dispatcher> = {};

function firstKey(key: unknown): string | undefined {
  return Array.isArray(key) && typeof key[0] === 'string' ? (key[0] as string) : undefined;
}

/**
 * Dispatch helper exported for tests. Given a mutationKey and outcome,
 * returns the events that would fire. Pure — no side effects.
 */
export function eventsFor(
  mutationKey: unknown,
  outcome: 'success' | 'error',
  data: unknown,
  vars: unknown,
  error: unknown,
): Array<{ name: EventName; properties: EventPayload<EventName> }> {
  const key = firstKey(mutationKey);
  if (!key) return [];
  const dispatcher = dispatchers[key];
  if (!dispatcher) return [];
  return dispatcher(outcome, data, vars, error) ?? [];
}

/**
 * Build a MutationCache that fires typed analytics events on every
 * mutation success/failure once, globally. Pass it to the QueryClient
 * constructor: `new QueryClient({ mutationCache: createAnalyticsMutationCache() })`.
 *
 * Routing to events is keyed off the Orval-generated mutationKey; see the
 * dispatchers map above.
 */
export function createAnalyticsMutationCache(): MutationCache {
  return new MutationCache({
    onSuccess: (data, vars, _ctx, mutation) => {
      const events = eventsFor(mutation.options.mutationKey, 'success', data, vars, undefined);
      for (const e of events) {
        getAnalyticsClient().track(e.name, e.properties);
      }
    },
    onError: (error, vars, _ctx, mutation) => {
      const events = eventsFor(mutation.options.mutationKey, 'error', undefined, vars, error);
      for (const e of events) {
        getAnalyticsClient().track(e.name, e.properties);
      }
    },
  });
}
